import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PurchaseNavButton, usePathwayPurchase } from './CheckoutFlow';
import { getPathway, setPendingPurchase } from '../lib/pathways';
import { API_BASE as BACKEND } from '../lib/apiBase';

const PATHWAY_NAMES = {
  'family-i130':      'Family-Based I-130',
  'aos-i485':         'Adjustment of Status (I-485)',
  'perm-employer':    'PERM / Employer-Sponsored',
  'eb2-niw':          'EB-2 National Interest Waiver',
  'eb1a':             'EB-1A Extraordinary Ability',
  'asylum':           'Asylum & Humanitarian',
  'f1-change-status': 'F-1 / Change of Status',
  'o1-nonimmigrant':  'O-1 Extraordinary Ability',
  'dv-lottery':       'Diversity Visa (DV Lottery)',
};

const PATHWAY_ICONS = {
  'family-i130':      'fa-users',
  'aos-i485':         'fa-id-card',
  'perm-employer':    'fa-briefcase',
  'eb2-niw':          'fa-graduation-cap',
  'eb1a':             'fa-trophy',
  'asylum':           'fa-shield-alt',
  'f1-change-status': 'fa-university',
  'o1-nonimmigrant':  'fa-star',
  'dv-lottery':       'fa-globe',
};

const PATHWAY_PRICES = {
  'family-i130': '$299', 'aos-i485': '$199', 'perm-employer': '$399',
  'eb2-niw': '$499', 'eb1a': '$499', 'asylum': '$299',
  'f1-change-status': '$249', 'o1-nonimmigrant': '$349', 'dv-lottery': '$299',
};

// AOS-qualifying pathways per spec Section 1
const AOS_QUALIFYING = new Set(['family-i130', 'perm-employer', 'eb2-niw', 'eb1a', 'asylum', 'dv-lottery']);

// Local scoring for unauthenticated users — covers all 9 pathways + AOS
const scoreLocally = (form) => {
  const results = [];
  const { status, education, employment, family, goal, prior_filings } = form;

  const isAdvanced = education === 'masters' || education === 'phd';
  const isPhd = education === 'phd';
  const hasExperience = employment === '4to7' || employment === '8plus';
  const hasExtensiveExp = employment === '8plus';
  const isCitizen = status === 'citizen';
  const isGreenCard = status === 'green_card';

  // --- Family I-130 ---
  if (family === 'yes' && (goal === 'green_card_family' || goal === 'green_card_self')) {
    results.push({
      pathway_id: 'family-i130', score: 90, confidence: 'high',
      reason: 'You have a qualifying US citizen or green card holder family member who could sponsor you for a family-based green card.',
    });
  } else if (family === 'yes') {
    results.push({
      pathway_id: 'family-i130', score: 60, confidence: 'medium',
      reason: 'You indicated a qualifying family member. Family-based sponsorship may be a viable parallel path.',
    });
  }

  // --- EB-2 NIW --- (requires advanced degree per USCIS)
  if (isAdvanced && (goal === 'green_card_self' || goal === 'green_card_employer')) {
    const score = (isPhd ? 40 : 25) + (hasExtensiveExp ? 30 : hasExperience ? 20 : 0) + 20;
    results.push({
      pathway_id: 'eb2-niw', score,
      confidence: (isPhd || hasExtensiveExp) ? 'high' : 'medium',
      reason: 'Your advanced degree and professional experience align with EB-2 NIW requirements. Self-petition without employer sponsorship.',
    });
  }

  // --- EB-1A --- (PhD + experience, or advanced + extensive experience)
  if ((isPhd && hasExperience) || (isAdvanced && hasExtensiveExp)) {
    let score = (isPhd ? 35 : 20) + (hasExtensiveExp ? 40 : 25) + 15;
    let conf = (isPhd && hasExtensiveExp) ? 'high' : 'medium';
    if (prior_filings === 'filed_approved' || prior_filings === 'filed_pending') {
      score += 10;
      conf = 'high';
    }
    results.push({
      pathway_id: 'eb1a', score, confidence: conf,
      reason: 'Your credentials suggest potential extraordinary ability. EB-1A requires meeting at least 3 of 10 USCIS criteria.',
    });
  }

  // --- PERM / Employer-Sponsored ---
  if (goal === 'green_card_employer') {
    const score = (status === 'h1b' ? 80 : 65);
    results.push({
      pathway_id: 'perm-employer', score,
      confidence: status === 'h1b' ? 'high' : 'medium',
      reason: 'Employer-sponsored PERM labor certification is the standard path when your employer will sponsor your green card.',
    });
  }

  // --- O-1 ---
  if ((hasExtensiveExp && goal === 'work_visa') || (isAdvanced && hasExtensiveExp && (goal === 'green_card_self' || goal === 'work_visa'))) {
    results.push({
      pathway_id: 'o1-nonimmigrant', score: 65, confidence: 'medium',
      reason: 'O-1 nonimmigrant visa may suit your experience level. Requires demonstrating extraordinary ability.',
    });
  }

  // --- Asylum ---
  if (goal === 'asylum') {
    results.push({
      pathway_id: 'asylum', score: 75, confidence: 'medium',
      reason: 'If you face persecution based on race, religion, nationality, political opinion, or social group, you may qualify for asylum.',
    });
  }

  // --- F-1 / Change of Status ---
  if (status === 'f1' || goal === 'study') {
    results.push({
      pathway_id: 'f1-change-status', score: status === 'f1' ? 80 : 65,
      confidence: status === 'f1' ? 'high' : 'medium',
      reason: 'F-1 Change of Status covers OPT extensions, STEM OPT, and status changes for students.',
    });
  }

  // --- DV Lottery --- (not for citizens or green card holders)
  if (!isCitizen && !isGreenCard) {
    results.push({
      pathway_id: 'dv-lottery', score: 30, confidence: 'low',
      reason: 'The Diversity Visa Lottery is available annually for nationals of eligible countries.',
    });
  }

  // --- AOS (I-485) --- recommend when user qualifies for an AOS-eligible pathway with medium+ confidence
  const qualifying = results.filter(r => AOS_QUALIFYING.has(r.pathway_id) && (r.confidence === 'high' || r.confidence === 'medium'));
  if (qualifying.length > 0 && !isCitizen && !isGreenCard) {
    const topName = PATHWAY_NAMES[qualifying[0].pathway_id] || qualifying[0].pathway_id;
    results.push({
      pathway_id: 'aos-i485', score: 45, confidence: 'medium',
      reason: `Since you may qualify for ${topName}, you can add Adjustment of Status (I-485) to adjust to permanent resident status without leaving the US. Discounted to $100 with a qualifying pathway.`,
    });
  }

  // Deduplicate and sort by score
  const seen = new Set();
  return results
    .filter(r => { if (seen.has(r.pathway_id)) return false; seen.add(r.pathway_id); return true; })
    .sort((a, b) => b.score - a.score)
    .slice(0, 7);
};

const EligibilityChecker = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    status: '',
    education: '',
    employment: '',
    family: '',
    goal: '',
    prior_filings: 'none',
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAuth, setShowAuth] = useState(false);

  const token = localStorage.getItem('token');

  const {
    beginPurchase,
    modal: checkoutModal,
    error: purchaseError,
  } = usePathwayPurchase({
    onNeedAuth: () => setShowAuth(true),
  });

  const topMatchId = results?.[0]?.pathway_id || results?.[0]?.pathway || null;
  const topMatch = topMatchId ? getPathway(topMatchId) : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setResults(null);

    // Persist answers for post-register profile import (AC 33)
    try {
      localStorage.setItem('eligibility_answers', JSON.stringify(form));
    } catch { /* ignore quota */ }

    // Always POST form answers to backend for server-side scoring
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`${BACKEND}/api/profile/eligibility-check`, {
        method: 'POST',
        headers,
        body: JSON.stringify(form),
      });

      let scored;
      if (res.ok) {
        const data = await res.json();
        const list = data.insights || [];
        if (list.length > 0) {
          scored = list.map(ins => ({
            pathway_id: ins.pathway_id,
            reason: ins.reason,
            confidence: ins.confidence,
            action: ins.recommended_action,
          }));
        } else {
          scored = scoreLocally(form);
        }
      } else {
        scored = scoreLocally(form);
      }
      setResults(scored);
      try {
        localStorage.setItem('eligibility_results', JSON.stringify(scored));
        if (scored[0]?.pathway_id) setPendingPurchase(scored[0].pathway_id);
      } catch { /* ignore */ }
    } catch {
      const scored = scoreLocally(form);
      setResults(scored);
      try {
        localStorage.setItem('eligibility_results', JSON.stringify(scored));
        if (scored[0]?.pathway_id) setPendingPurchase(scored[0].pathway_id);
      } catch { /* ignore */ }
    }

    setLoading(false);
  };

  const field = (name, value) => setForm(prev => ({ ...prev, [name]: value }));

  const handlePurchase = (pathwayId) => {
    setPendingPurchase(pathwayId);
    beginPurchase(pathwayId);
  };

  const confidenceBadge = (conf) => {
    if (conf === 'high') return <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-green-100 text-green-700">High Match</span>;
    if (conf === 'medium') return <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-yellow-100 text-yellow-700">Medium Match</span>;
    return <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-gray-100 text-gray-600">Worth Exploring</span>;
  };

  return (
    <div className="min-h-screen bg-primary">
      {/* Nav */}
      <nav className="bg-primary border-b border-highlight">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-3">
          <a href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <div className="w-8 h-8 bg-accent rounded-full flex items-center justify-center">
              <i className="fas fa-balance-scale text-primary text-sm"></i>
            </div>
            <span className="text-secondary font-bold text-xl font-cormorant">ImmigrateSmart</span>
          </a>
          <div className="flex items-center gap-3">
            <PurchaseNavButton />
            {token ? (
              <button onClick={() => navigate('/dashboard')} className="border border-white/30 text-secondary px-4 py-2 rounded-lg text-sm font-semibold hover:border-accent hover:text-accent transition-colors">
                Dashboard
              </button>
            ) : (
              <button onClick={() => setShowAuth(true)} className="text-secondary hover:text-accent transition-colors text-sm font-medium">
                Sign In
              </button>
            )}
          </div>
        </div>
      </nav>

      <div className={`max-w-3xl mx-auto px-4 sm:px-6 py-16 ${results ? "pb-32" : ""}`}>
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-secondary font-cormorant mb-4">
            Check Your <span className="text-accent">Eligibility</span>
          </h1>
          <p className="text-secondary/70 text-lg">
            Free estimate — no account required. Already know your pathway?{' '}
            <a href="/pricing" className="text-accent underline font-semibold">Buy directly</a>
            {' '}without this quiz.
          </p>
          <p className="text-secondary/40 text-sm mt-2">
            This is an eligibility estimate, not legal advice.
          </p>
        </div>

        {!results ? (
          <form onSubmit={handleSubmit} className="bg-secondary rounded-2xl p-5 sm:p-8 shadow-xl space-y-6">
            {/* Current immigration status */}
            <div>
              <label className="block font-semibold text-primary mb-3">Current immigration status in the US</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {[
                  ['citizen', 'US Citizen'],
                  ['green_card', 'Green Card Holder'],
                  ['h1b', 'H-1B / Work Visa'],
                  ['f1', 'F-1 Student'],
                  ['other_visa', 'Other Visa'],
                  ['outside_us', 'Outside the US'],
                ].map(([val, label]) => (
                  <label key={val} className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all text-sm ${form.status === val ? 'border-accent bg-accent/10' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" name="status" value={val} onChange={() => field('status', val)} className="accent-yellow-500" required />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {/* Education */}
            <div>
              <label className="block font-semibold text-primary mb-3">Highest level of education</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  ['highschool', 'High School'],
                  ['bachelors', "Bachelor's"],
                  ['masters', "Master's"],
                  ['phd', 'PhD / Doctorate'],
                ].map(([val, label]) => (
                  <label key={val} className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all text-sm ${form.education === val ? 'border-accent bg-accent/10' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" name="education" value={val} onChange={() => field('education', val)} className="accent-yellow-500" required />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {/* Employment */}
            <div>
              <label className="block font-semibold text-primary mb-3">Years of professional experience in your field</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  ['none', 'None'],
                  ['1to3', '1\u20133 years'],
                  ['4to7', '4\u20137 years'],
                  ['8plus', '8+ years'],
                ].map(([val, label]) => (
                  <label key={val} className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all text-sm ${form.employment === val ? 'border-accent bg-accent/10' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" name="employment" value={val} onChange={() => field('employment', val)} className="accent-yellow-500" required />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {/* Family ties */}
            <div>
              <label className="block font-semibold text-primary mb-3">Do you have a US citizen or green card holder family member who could sponsor you?</label>
              <div className="flex gap-4">
                {[['yes', 'Yes'], ['no', 'No'], ['unsure', 'Not sure']].map(([val, label]) => (
                  <label key={val} className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all text-sm ${form.family === val ? 'border-accent bg-accent/10' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" name="family" value={val} onChange={() => field('family', val)} className="accent-yellow-500" required />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {/* Prior filings */}
            <div>
              <label className="block font-semibold text-primary mb-3">Have you previously filed any US immigration petitions?</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  ['none', 'No prior filings'],
                  ['filed_approved', 'Yes \u2014 approved'],
                  ['filed_pending', 'Yes \u2014 pending'],
                  ['filed_denied', 'Yes \u2014 denied'],
                ].map(([val, label]) => (
                  <label key={val} className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all text-sm ${form.prior_filings === val ? 'border-accent bg-accent/10' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" name="prior_filings" value={val} onChange={() => field('prior_filings', val)} className="accent-yellow-500" />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {/* Goal */}
            <div>
              <label className="block font-semibold text-primary mb-3">What is your primary immigration goal?</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {[
                  ['green_card_self', 'Self-petition for a green card'],
                  ['green_card_employer', 'Employer-sponsored green card'],
                  ['green_card_family', 'Family-sponsored green card'],
                  ['work_visa', 'Work visa / nonimmigrant status'],
                  ['study', 'Study in the US'],
                  ['asylum', 'Seek asylum / humanitarian protection'],
                ].map(([val, label]) => (
                  <label key={val} className={`flex items-center gap-2 p-3 border-2 rounded-lg cursor-pointer transition-all text-sm ${form.goal === val ? 'border-accent bg-accent/10' : 'border-gray-200 hover:border-gray-300'}`}>
                    <input type="radio" name="goal" value={val} onChange={() => field('goal', val)} className="accent-yellow-500" required />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-accent text-primary py-4 rounded-xl font-semibold text-lg hover:bg-accent/90 transition-colors disabled:opacity-50"
            >
              {loading ? <><i className="fas fa-spinner fa-spin mr-2"></i>Analyzing...</> : 'Check My Eligibility'}
            </button>
          </form>
        ) : (
          <div>
            <div className="bg-secondary rounded-2xl p-6 shadow-xl mb-8">
              <h2 className="text-2xl font-bold text-primary font-cormorant mb-2">Your Eligibility Results</h2>
              <p className="text-primary/60 text-sm">Based on your answers, here are your most likely qualifying pathways:</p>
            </div>

            <div className="space-y-4">
              {results.length === 0 ? (
                <div className="bg-secondary rounded-xl p-6 text-center text-primary/60">
                  No strong pathway matches found. Consider consulting with an immigration attorney or
                  {' '}<a href="/pricing" className="text-accent underline">browsing all pathways</a>.
                </div>
              ) : results.map((r, i) => {
                const pid = r.pathway_id || r.pathway;
                const name = PATHWAY_NAMES[pid] || pid;
                const icon = PATHWAY_ICONS[pid] || 'fa-file-alt';
                const price = PATHWAY_PRICES[pid] || '';
                const conf = r.confidence || 'medium';
                return (
                  <div key={pid || i} className="bg-secondary rounded-xl p-5 shadow flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${i === 0 ? 'bg-accent text-primary' : 'bg-accent/20 text-accent'}`}>
                      <i className={`fas ${icon} text-lg`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {i === 0 && <span className="bg-accent text-primary text-xs px-2 py-0.5 rounded-full font-semibold">Best Match</span>}
                        {confidenceBadge(conf)}
                        <h3 className="font-bold text-primary">{name}</h3>
                        {price && <span className="text-accent font-semibold text-sm">{price}</span>}
                      </div>
                      <p className="text-primary/70 text-sm mt-1">{r.reason || r.reasoning || r.recommendation || ''}</p>
                      {r.action && <p className="text-accent/80 text-xs mt-1 font-medium">{r.action}</p>}
                    </div>
                    <button
                      type="button"
                      onClick={() => handlePurchase(pid)}
                      className="flex-shrink-0 bg-accent text-primary px-4 py-2.5 rounded-lg text-sm font-bold hover:bg-accent/90 transition-colors whitespace-nowrap"
                    >
                      {price ? `Purchase ${price}` : 'Purchase'}
                    </button>
                  </div>
                );
              })}
            </div>

            {(purchaseError) && (
              <p className="mt-4 text-center text-red-600 text-sm">{purchaseError}</p>
            )}

            {topMatch && (
              <div className="mt-8 bg-accent/15 border-2 border-accent rounded-2xl p-6 text-center">
                <p className="text-xs tracking-widest uppercase text-accent font-bold mb-2">Ready to continue?</p>
                <h3 className="text-xl font-cormorant font-bold text-primary mb-2">
                  Continue to {topMatch.name}
                </h3>
                <p className="text-primary/70 text-sm mb-4">
                  One-time purchase {topMatch.price}. Unlock the full EB workflow, Evidence Builder, and documents.
                </p>
                <button
                  type="button"
                  onClick={() => handlePurchase(topMatch.id)}
                  className="w-full sm:w-auto bg-accent text-primary px-8 py-3.5 rounded-lg text-sm font-bold tracking-wide uppercase hover:bg-accent/90 transition-colors"
                >
                  Continue to {topMatch.name} — {topMatch.price}
                </button>
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-4 justify-center">
              <button
                type="button"
                onClick={() => setResults(null)}
                className="px-6 py-3 border border-gray-300 text-primary/70 rounded-lg font-semibold hover:bg-gray-50 transition-colors text-sm"
              >
                <i className="fas fa-redo mr-2"></i>Start Over
              </button>
              <a
                href="/pricing"
                className="px-6 py-3 bg-primary text-secondary rounded-lg font-semibold hover:bg-primary/90 transition-colors text-sm"
              >
                Or buy any pathway directly
              </a>
            </div>

            <p className="text-center text-primary/40 text-xs mt-6">
              This eligibility estimate is for informational purposes only and does not constitute legal advice.
              Eligibility determinations are made by USCIS based on full case review.
            </p>
          </div>
        )}
      </div>

      {showAuth && <AuthRedirectNote onClose={() => setShowAuth(false)} />}
      {checkoutModal}
    </div>
  );
};

// Small note prompting sign-in to proceed to workflow
const AuthRedirectNote = ({ onClose }) => (
  <div className="fixed inset-0 bg-[#303035]/75 flex items-center justify-center z-[70] p-4">
    <div className="bg-white rounded-2xl p-8 w-full max-w-md text-center shadow-2xl">
      <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <i className="fas fa-credit-card text-yellow-600 text-2xl"></i>
      </div>
      <h3 className="text-2xl font-bold text-black mb-3 font-cormorant">Sign In to Purchase</h3>
      <p className="text-gray-600 mb-6">
        Create a free account or sign in to continue to payment. Your eligibility answers will be saved — then we take you straight to checkout.
      </p>
      <div className="flex gap-3">
        <a href="/?auth=1&from=eligibility&purchase=1" className="flex-1 bg-yellow-500 text-black py-3 rounded-lg font-semibold hover:bg-yellow-400 transition-colors">
          Sign In / Register
        </a>
        <button type="button" onClick={onClose} className="px-4 py-3 border border-gray-300 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">
          Cancel
        </button>
      </div>
    </div>
  </div>
);

export default EligibilityChecker;
